import type { HTMLAttributes } from 'react';
import { GripVertical, Star, User, Users } from 'lucide-react';

import { cn } from 'lib/utils';

import type { ReportDefinition } from './mock/reports';
import { ReportCategoryBadge } from './ReportCategoryBadge';

type ReportLibraryItemProps = {
    report: ReportDefinition;
    isActive: boolean;
    showCategoryBadge: boolean;
    onSelect: (reportId: string) => void;
    onToggleFavorite: (reportId: string) => void;
    /** Favorite star for built-in reports; user/users icons fill the gap for custom views. */
    leadingIcon?: 'favorite' | 'custom' | 'shared';
    dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
    isDragging?: boolean;
};

export function ReportLibraryItem({
    report,
    isActive,
    showCategoryBadge,
    onSelect,
    onToggleFavorite,
    leadingIcon = 'favorite',
    dragHandleProps,
    isDragging = false,
}: ReportLibraryItemProps) {
    const isFavourite = Boolean(report.isFavourite);
    const canReorder = Boolean(dragHandleProps);
    const showFavoriteStar = leadingIcon === 'favorite';
    const LeadingMetaIcon =
        leadingIcon === 'custom' ? User : leadingIcon === 'shared' ? Users : null;
    const metaIndentClass = LeadingMetaIcon ? 'pl-[26px]' : undefined;
    const hasMetaBelow = showCategoryBadge || Boolean(report.sharedWithTeam);

    return (
        <div
            className={cn(
                'reports-library-item group flex w-full rounded-lg border p-2.5 text-left transition-colors',
                hasMetaBelow ? 'items-start' : 'items-center',
                isActive && 'reports-library-item-active',
                isDragging && 'relative z-50 shadow-md ring-2 ring-primary/20',
            )}
        >
            {canReorder ? (
                <button
                    type="button"
                    className={cn(
                        '-ml-1 mr-0.5 shrink-0 touch-none appearance-none border-0 bg-transparent p-0 text-muted-foreground shadow-none',
                        hasMetaBelow && 'mt-0.5',
                        'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
                        isDragging && 'opacity-100',
                    )}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                    aria-label={`Drag ${report.title}`}
                    {...dragHandleProps}
                >
                    <GripVertical className="h-4 w-4" />
                </button>
            ) : null}

            <button
                type="button"
                onClick={() => onSelect(report.id)}
                className="reports-library-item-select flex min-w-0 flex-1 cursor-pointer flex-col gap-1.5 appearance-none border-0 bg-transparent p-0 text-left shadow-none"
            >
                <div className="flex items-center gap-2.5">
                    {LeadingMetaIcon ? (
                        <span
                            className="shrink-0 text-muted-foreground/70"
                            title={leadingIcon === 'custom' ? 'Custom view' : 'Shared view'}
                            aria-label={leadingIcon === 'custom' ? 'Custom view' : 'Shared view'}
                        >
                            <LeadingMetaIcon className="h-4 w-4" />
                        </span>
                    ) : null}

                    <span className="reports-library-item-title min-w-0 text-[13px] font-medium leading-none text-foreground/80">
                        {report.title}
                    </span>
                </div>

                {showCategoryBadge && (
                    <div
                        className={cn(
                            'flex min-w-0 flex-wrap items-center gap-1.5',
                            metaIndentClass,
                        )}
                    >
                        <ReportCategoryBadge category={report.category} />
                        {report.viewedLabel ? (
                            <span className="text-xs text-muted-foreground">
                                {report.viewedLabel}
                            </span>
                        ) : null}
                    </div>
                )}

                {report.sharedWithTeam && (
                    <div className={metaIndentClass}>
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            <Users className="h-3 w-3" />
                            Shared with team
                        </span>
                    </div>
                )}
            </button>

            {showFavoriteStar ? (
                <button
                    type="button"
                    aria-label={isFavourite ? 'Remove from favorites' : 'Add to favorites'}
                    onClick={() => onToggleFavorite(report.id)}
                    className={cn(
                        'reports-library-item-favorite ml-1 shrink-0 cursor-pointer appearance-none rounded-sm border-0 bg-transparent p-0.5 text-muted-foreground/70 shadow-none transition-colors hover:text-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        hasMetaBelow && 'mt-0.5',
                    )}
                >
                    <Star
                        className={cn(
                            'h-4 w-4',
                            isFavourite ? 'fill-amber-400 text-amber-400' : 'fill-none',
                        )}
                    />
                </button>
            ) : null}
        </div>
    );
}
